/**
 * profileCompletion.js — THE single source of truth for "is this profile section
 * complete" and "what percentage is the profile".
 *
 * WHY THIS FILE EXISTS (UX-1 / TF-02 / TF-03 / TF-05, 2026-07-31).
 * Three TestFlight reports said the profile percentage was wrong. It was, and the
 * cause was that the app carried FOUR different definitions of "identification is
 * complete", none of which agreed:
 *
 *   OnboardingFlow.computeCompletion  firstName lastName dob sex zipCode email phone   (7)
 *   ProfileScreen.isComplete          firstName lastName dob                           (3)
 *   ProfileIdentification.canSave     firstName lastName dob sex street city state zip (8)
 *   OnboardingIdentity address fields rendered only when BOTH ID sides were uploaded
 *
 * The comment above the onboarding copy claimed v242.9.0 made it "mirror
 * ProfileScreen's headline ... so the two never disagree." It never mirrored it —
 * seven fields against three. A comment asserting a property the code does not
 * have is why session 16 closed UX-1 from git history and two devices reopened it
 * the same day.
 *
 * The user-visible failure was worse than a wrong number: the headline said
 * identification was complete (3 fields), while the identification FORM refused
 * to save without street/city/state (8 fields) — and onboarding never asked for
 * those unless the patient happened to upload both sides of their ID. That is
 * TF-05, "address line not complete", arriving from the same root.
 *
 * THE DEFINITION IS THE FORM'S, deliberately. Of the four, the form's is the one
 * that actually gates the patient — a headline that reads 100% over a section the
 * patient cannot save is not optimistic, it is wrong. Making the count stricter
 * means some existing profiles drop below 100%; that is the honest number and it
 * is the one the reports are asking for.
 *
 * ADDING A FIELD? Add it HERE, once. If you find yourself writing a completeness
 * check anywhere else in the app, that is the bug this file was created to end.
 */

// The four sections that count toward the headline percentage, in display order.
export const REQUIRED_SECTIONS = ['identification', 'insurance', 'allergies', 'medications']
// ★ s95 sign-up rebuild: sign-up no longer asks for insurance, allergies or medications, so
//   THIS is the check that keeps a case from being shared without them. Every share path
//   (send to an Orella doctor, share code / fax) asks it; the server asks it again.
export function missingBeforeShare(profile) {
  return REQUIRED_SECTIONS.filter((id) => !isSectionComplete(id, profile))
}

export const OPTIONAL_SECTIONS = ['medical-history', 'family-history', 'social-lifestyle']

// Identification is the only section defined by individual fields rather than by
// a `completed` flag, because it is the only one a patient can partially fill from
// three different entry points (onboarding, the form, and ID-card OCR).
export const IDENTIFICATION_REQUIRED_FIELDS = [
  'firstName', 'lastName', 'dob', 'sex', 'streetAddress', 'city', 'state', 'zipCode',
]

const nonEmpty = (v) => v !== null && v !== undefined && String(v).trim() !== ''

/** Which identification fields are still missing. Drives both the gate and the copy. */
export function missingIdentificationFields(identification) {
  const id = identification || {}
  return IDENTIFICATION_REQUIRED_FIELDS.filter((f) => !nonEmpty(id[f]))
}

export function isIdentificationComplete(identification) {
  return missingIdentificationFields(identification).length === 0
}

/**
 * Is one section complete, given the PROFILE STORE shape?
 * `id` is a SECTION_DEFS id ('identification', 'insurance', 'medical-history', ...).
 */
export function isSectionComplete(id, profile) {
  const p = profile || {}
  switch (id) {
    case 'identification':   return isIdentificationComplete(p.identification)
    // Self-pay is a complete answer to the insurance question, not a skipped one.
    case 'insurance':        return !!(p.insurance?.completed || p.insurance?.selfPay)
    case 'allergies':        return !!p.allergies?.completed
    case 'medications':      return !!p.medications?.completed
    case 'medical-history':  return !!p.medicalHistory?.completed
    case 'family-history':   return !!p.familyHistory?.completed
    // ★★★ v242.150.0 SOCIAL-COMPLETE-EARNED-1 — DERIVED, NOT STORED. `completed`
    //   was set by pressing Save, whatever the section contained, so a card with
    //   two empty fields wore a green tick — and cannabis and occupation were
    //   never asked at signup at all. His ruling: all five are required. Deriving
    //   it also means an older row cannot keep claiming a completeness it never
    //   had, which storing the flag allowed.
    case 'social-lifestyle': {
      const s = p.socialLifestyle || {}
      return !!(s.smoking && s.alcohol && s.cannabis && s.exercise
                && String(s.occupation || '').trim())
    }
    default:                 return false
  }
}

/**
 * How complete ONE section is, 0..1.
 *
 * DECISION 52 (v242.62.0) — "we need an accurate percent complete."
 * Counting whole sections gave a headline that could only ever read 0 / 25 / 50 /
 * 75 / 100, because there are four required sections. A patient who had filled
 * SEVEN of the eight identification fields scored the same as one who had filled
 * NONE. That is not a rounding artefact, it is the number being wrong: it moved
 * only on section boundaries and sat still through most of the actual work.
 *
 * Identification now contributes PRO-RATA over its fields. The other three are a
 * single yes/no question each ("is there insurance", "are there allergies",
 * "are there medications" — where an explicit "none" is an answer), so they stay
 * 0 or 1; there is nothing to pro-rate.
 *
 * Sections stay EQUALLY WEIGHTED at 25% each rather than counting all 11 required
 * fields in one pool — under a flat field count identification alone would be 8/11
 * = 73% of the bar, and the four rows the patient sees on screen would no longer
 * correspond to four equal quarters of it.
 */
export function sectionCompletionFraction(id, profile) {
  const p = profile || {}
  if (id === 'identification') {
    const total = IDENTIFICATION_REQUIRED_FIELDS.length
    const missing = missingIdentificationFields(p.identification).length
    return (total - missing) / total
  }
  return isSectionComplete(id, profile) ? 1 : 0
}

/** The headline number: required sections only. Optional ones are tracked separately. */
export function requiredCompletionPercent(profile) {
  const sum = REQUIRED_SECTIONS.reduce((acc, id) => acc + sectionCompletionFraction(id, profile), 0)
  const pct = (sum / REQUIRED_SECTIONS.length) * 100
  // Never round a partially-filled profile up to 100 or down to 0 — the headline
  // must not claim "done" over an unsaveable section, and must not read "nothing
  // started" to a patient who has started. Those two ends are load-bearing; the
  // middle is cosmetic.
  if (pct >= 99.5 && !allRequiredComplete(profile)) return 99
  if (pct > 0 && pct < 1) return 1
  return Math.round(pct)
}

export function optionalCompletionPercent(profile) {
  if (!OPTIONAL_SECTIONS.length) return 0
  const done = OPTIONAL_SECTIONS.filter((id) => isSectionComplete(id, profile)).length
  return Math.round((done / OPTIONAL_SECTIONS.length) * 100)
}

export function allRequiredComplete(profile) {
  return REQUIRED_SECTIONS.every((id) => isSectionComplete(id, profile))
}

/**
 * Onboarding adapter.
 *
 * The onboarding store holds its answers in a different shape from the profile
 * store, so the Done screen used to carry its own copy of the arithmetic — which
 * is precisely how the four definitions drifted apart. Project the onboarding
 * state into the profile shape and then run the SAME functions above, so the
 * number on the Done screen and the number on the profile cannot disagree by
 * construction rather than by a comment promising that they won't.
 */
export function onboardingStateToProfileShape(state) {
  const s = state || {}
  const identity = s.identity || {}
  const insurance = s.insurance || {}
  const allergies = s.allergies || {}
  const medications = s.medications || {}
  return {
    identification: {
      firstName: identity.firstName,
      lastName: identity.lastName,
      dob: identity.dob,
      sex: identity.sex,
      streetAddress: identity.streetAddress,
      city: identity.city,
      state: identity.state,
      zipCode: identity.zipCode,
    },
    // v242.44.0 fixed `carrier` -> `provider` here; keep reading `provider`.
    insurance: { completed: !!insurance.provider, selfPay: !!insurance.selfPay },
    // An explicit "no known allergies" / "no medications" is an answer.
    allergies: { completed: !!((allergies.items || []).length > 0 || allergies.noKnown) },
    medications: { completed: !!((medications.items || []).length > 0 || medications.none) },
  }
}

